import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useBookingContext } from '../BookingContext'
import { Button } from '@w/components/ui/button'
import { Input } from '@w/components/ui/input'
import { Label } from '@w/components/ui/label'
import { Textarea } from '@w/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@w/components/ui/select'
import { Checkbox } from '@w/components/ui/checkbox'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@w/components/ui/form'
import { Card, CardContent } from '@w/components/ui/card'
import { UserCircle, Mail, Phone, MessageSquare, HelpCircle, ArrowRight, ArrowLeft, Bell } from 'lucide-react'
import CustomQuestions, { CustomQuestion } from '../components/CustomQuestions'
import FieldNodeWrapper from '../components/inline-editor/FieldNodeWrapper'
import AddFieldButton from '../components/inline-editor/AddFieldButton'

export default function ContactStep() {
  const {
    config,
    customerInfo,
    setCustomerInfo,
    goToStep,
    configurationMode,
    // Inline editor state & actions
    selectedFieldId,
    selectField,
    updateFieldVisibility,
    addCustomField,
    removeCustomField,
  } = useBookingContext()
  const fieldLabels = config.widgetConfig.field_labels || {}
  const customQuestions: CustomQuestion[] = config.widgetConfig.custom_questions || []

  // Visibility flags (default to true if not set for backwards compatibility)
  const showNotes = config.widgetConfig.show_notes ?? true
  const showAddress = config.widgetConfig.show_address ?? false
  const showHowHeard = config.widgetConfig.show_how_heard ?? true
  const showSmsConsent = config.widgetConfig.show_sms_consent ?? true

  // Configuration mode: Show static form structure
  if (configurationMode) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <UserCircle className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Your Information</h2>
            <p className="text-sm text-muted-foreground">Let us know how to reach you</p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Name and Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name field */}
              <FieldNodeWrapper
                fieldId="name"
                isSelected={selectedFieldId === 'name'}
                onSelect={selectField}
              >
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-muted-foreground" />
                    {fieldLabels.name_label || 'Full Name'} <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm cursor-pointer items-center">
                    <span className="text-muted-foreground">{fieldLabels.name_placeholder || 'Enter your full name'}</span>
                  </div>
                </div>
              </FieldNodeWrapper>

              {/* Email field */}
              <FieldNodeWrapper
                fieldId="email"
                isSelected={selectedFieldId === 'email'}
                onSelect={selectField}
              >
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {fieldLabels.email_label || 'Email Address'} <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm cursor-pointer items-center">
                    <span className="text-muted-foreground">{fieldLabels.email_placeholder || 'Enter your email'}</span>
                  </div>
                </div>
              </FieldNodeWrapper>
            </div>

            {/* Phone */}
            <FieldNodeWrapper
              fieldId="phone"
              isSelected={selectedFieldId === 'phone'}
              onSelect={selectField}
            >
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  {fieldLabels.phone_label || 'Phone Number'} <span className="text-destructive">*</span>
                </Label>
                <div className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm cursor-pointer items-center">
                  <span className="text-muted-foreground">{fieldLabels.phone_placeholder || '(555) 123-4567'}</span>
                </div>
              </div>
            </FieldNodeWrapper>

            {/* Notes */}
            {showNotes && (
              <FieldNodeWrapper
                fieldId="notes"
                isSelected={selectedFieldId === 'notes'}
                isRemovable={true}
                onSelect={selectField}
                onDelete={() => {
                  updateFieldVisibility('notes', false)
                }}
              >
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                    {fieldLabels.notes_label || 'Additional Notes'}
                  </Label>
                  <div className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm cursor-pointer">
                    <span className="text-muted-foreground">{fieldLabels.notes_placeholder || 'Any special instructions or details...'}</span>
                  </div>
                </div>
              </FieldNodeWrapper>
            )}

            {/* How did you hear */}
            {showHowHeard && (
              <FieldNodeWrapper
                fieldId="how_heard"
                isSelected={selectedFieldId === 'how_heard'}
                isRemovable={true}
                onSelect={selectField}
                onDelete={() => {
                  updateFieldVisibility('how_heard', false)
                }}
              >
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-muted-foreground" />
                    {fieldLabels.how_heard_label || 'How did you hear about us?'}
                  </Label>
                  <Select>
                    <SelectTrigger className="h-11 pointer-events-none cursor-pointer opacity-60">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                  </Select>
                </div>
              </FieldNodeWrapper>
            )}

            {/* SMS Consent */}
            {showSmsConsent && (
              <FieldNodeWrapper
                fieldId="sms_consent"
                isSelected={selectedFieldId === 'sms_consent'}
                isRemovable={false}
                onSelect={selectField}
              >
                <div className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-4 bg-muted/30">
                  <Checkbox
                    className="mt-0.5 pointer-events-none opacity-60"
                    checked={false}
                  />
                  <div className="space-y-1 leading-none flex-1">
                    <Label className="font-normal flex items-center gap-2 cursor-pointer">
                      <Bell className="w-4 h-4 text-muted-foreground" />
                      {fieldLabels.sms_consent_label || 'I agree to receive SMS notifications about my appointment'}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      We'll send you appointment reminders and updates
                    </p>
                  </div>
                </div>
              </FieldNodeWrapper>
            )}

            {/* Custom Questions - Configuration display */}
            {customQuestions.filter(q => q.show_on_step === 'contact').length > 0 && (
              <div className="pt-4 border-t space-y-4">
                {customQuestions
                  .filter(q => q.show_on_step === 'contact')
                  .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                  .map(question => (
                    <FieldNodeWrapper
                      key={question.id}
                      fieldId={question.id}
                      isSelected={selectedFieldId === question.id}
                      isRemovable={true}
                      onSelect={selectField}
                      onDelete={removeCustomField}
                    >
                      <div className="space-y-2">
                        <Label>
                          {question.label} {question.required && <span className="text-destructive">*</span>}
                        </Label>
                        {question.type === 'text' && (
                          <div className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm cursor-pointer items-center">
                            <span className="text-muted-foreground">{question.placeholder || 'Enter value...'}</span>
                          </div>
                        )}
                        {question.type === 'number' && (
                          <div className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm cursor-pointer items-center">
                            <span className="text-muted-foreground">{question.placeholder || '0'}</span>
                          </div>
                        )}
                        {question.type === 'textarea' && (
                          <div className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm cursor-pointer">
                            <span className="text-muted-foreground">{question.placeholder || 'Enter value...'}</span>
                          </div>
                        )}
                        {(question.type === 'select' || question.type === 'radio') && (
                          <Select>
                            <SelectTrigger className="pointer-events-none cursor-pointer opacity-60">
                              <SelectValue placeholder={question.placeholder || 'Select an option'} />
                            </SelectTrigger>
                          </Select>
                        )}
                        {question.type === 'checkbox' && (
                          <div className="flex items-center space-x-2">
                            <Checkbox className="pointer-events-none opacity-60" checked={false} />
                            <span className="text-sm text-muted-foreground">{question.placeholder}</span>
                          </div>
                        )}
                        {question.help_text && (
                          <p className="text-xs text-muted-foreground">{question.help_text}</p>
                        )}
                      </div>
                    </FieldNodeWrapper>
                  ))}
              </div>
            )}

            {/* Add Field Button */}
            <AddFieldButton
              onAddField={(fieldType) => addCustomField(fieldType, 'contact')}
              position="end"
            />

            {/* Buttons - Disabled in config mode */}
            <div className="flex gap-4 justify-between pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Button type="button" size="lg" disabled className="gap-2">
                Review Booking
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Build dynamic schema with custom questions
  const contactSchema = useMemo(() => {
    const baseSchema = {
      name: z.string().min(1, 'Name is required'),
      email: z.string().email('Invalid email address'),
      phone: z.string().min(10, 'Valid phone number required'),
      notes: z.string().optional(),
      howHeard: z.string().optional(),
      smsConsent: z.boolean().default(false),
    }

    // Add custom fields to schema
    const customFieldsSchema: Record<string, z.ZodTypeAny> = {}

    customQuestions
      .filter((q) => q.show_on_step === 'contact')
      .forEach((question) => {
        if (question.type === 'checkbox') {
          customFieldsSchema[question.id] = question.required
            ? z.boolean().refine((val) => val === true, { message: `${question.label} is required` })
            : z.boolean().optional()
        } else {
          let field = z.string()
          if (question.validation?.min_length) {
            field = field.min(question.validation.min_length)
          }
          if (question.validation?.max_length) {
            field = field.max(question.validation.max_length)
          }
          customFieldsSchema[question.id] = question.required
            ? field.min(1, `${question.label} is required`)
            : field.optional()
        }
      })

    return z.object({
      ...baseSchema,
      customFields: z.object(customFieldsSchema).optional(),
    })
  }, [customQuestions])

  type ContactFormData = z.infer<typeof contactSchema>

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: customerInfo?.name || '',
      email: customerInfo?.email || '',
      phone: customerInfo?.phone || '',
      notes: customerInfo?.notes || '',
      howHeard: customerInfo?.howHeard || '',
      smsConsent: customerInfo?.smsConsent || false,
      customFields: customerInfo?.customFields || {},
    },
  })

  const onSubmit = (data: ContactFormData) => {
    setCustomerInfo({
      name: data.name,
      email: data.email,
      phone: formatPhone(data.phone),
      notes: data.notes,
      howHeard: data.howHeard,
      smsConsent: data.smsConsent,
      customFields: data.customFields || {},
    })
    goToStep('confirm')
  }

  // Format phone number as user types
  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`
    }
    return value
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <UserCircle className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold">Your Information</h2>
          <p className="text-sm text-muted-foreground">Let us know how to reach you</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Name and Email Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-muted-foreground" />
                    {fieldLabels.name_label || 'Full Name'} <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={fieldLabels.name_placeholder || 'Enter your full name'} className="h-11" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {fieldLabels.email_label || 'Email Address'} <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder={fieldLabels.email_placeholder || 'Enter your email'} className="h-11" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Phone */}
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  {fieldLabels.phone_label || 'Phone Number'} <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    placeholder={fieldLabels.phone_placeholder || '(555) 123-4567'}
                    className="h-11"
                    {...field}
                    onChange={(e) => {
                      const formatted = formatPhone(e.target.value)
                      field.onChange(formatted)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Notes */}
          {showNotes && (
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                    {fieldLabels.notes_label || 'Additional Notes'}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={fieldLabels.notes_placeholder || 'Any special instructions or details...'}
                      rows={3}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* How did you hear */}
          {showHowHeard && (
            <FormField
              control={form.control}
              name="howHeard"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-muted-foreground" />
                    {fieldLabels.how_heard_label || 'How did you hear about us?'}
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="google">Google Search</SelectItem>
                      <SelectItem value="referral">Friend/Family Referral</SelectItem>
                      <SelectItem value="social">Social Media</SelectItem>
                      <SelectItem value="website">Your Website</SelectItem>
                      <SelectItem value="advertisement">Advertisement</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* SMS Consent */}
          {showSmsConsent && (
            <FormField
              control={form.control}
              name="smsConsent"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-4 bg-muted/30">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
                  </FormControl>
                  <div className="space-y-1 leading-none flex-1">
                    <FormLabel className="font-normal flex items-center gap-2 cursor-pointer">
                      <Bell className="w-4 h-4 text-muted-foreground" />
                      I agree to receive SMS notifications about my appointment
                    </FormLabel>
                    <p className="text-xs text-muted-foreground">
                      We'll send you appointment reminders and updates
                    </p>
                  </div>
                </FormItem>
              )}
            />
          )}

          {/* Custom Questions */}
          {customQuestions.length > 0 && (
            <div className="pt-4 border-t">
              <CustomQuestions
                questions={customQuestions}
                control={form.control}
                step="contact"
              />
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4 justify-between pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => goToStep('service')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button type="submit" size="lg" className="gap-2">
              Review Booking
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </Form>
        </CardContent>
      </Card>
    </div>
  )
}
